import { apiCall, type ApiClientConfig } from "@playbookai/api-clients";
import type {
  DashboardRecord,
  DashboardType,
  WidgetDataResolveResponse,
  WidgetDataResolveRequest,
  WidgetKind,
  WidgetWidth,
} from "@playbookai/types";

/**
 * The /v1/dashboards API surface (plan §11). Every mutation carries
 * `expectedDashboardVersion` (plus its row-specific expected version) and
 * returns the FULL authoritative DashboardRecord, so callers reconcile the
 * cache from the response instead of patching fragments.
 */

const seg = encodeURIComponent;

export interface EnsureDashboardInput {
  readonly dashboardType: DashboardType;
  readonly subjectId?: string;
}

export interface CreateWidgetInstanceInput {
  readonly expectedDashboardVersion: number;
  readonly expectedPageVersion: number;
  readonly widgetKind: WidgetKind;
  readonly titleOverride?: string;
  readonly params?: Record<string, unknown>;
  readonly width?: WidgetWidth;
}

export interface UpdateWidgetInstanceInput {
  readonly expectedDashboardVersion: number;
  readonly expectedWidgetVersion: number;
  readonly titleOverride?: string | null;
  readonly params?: Record<string, unknown>;
}

export interface WidgetLayoutItemInput {
  readonly widgetInstanceId: string;
  readonly displayOrder: number;
  readonly width: WidgetWidth;
}

export interface WidgetLayoutInput {
  readonly expectedDashboardVersion: number;
  readonly expectedPageVersion: number;
  readonly items: readonly WidgetLayoutItemInput[];
}

export interface PutConnectionInput {
  readonly expectedDashboardVersion: number;
  readonly expectedConnectionVersion?: number;
  readonly selection: Record<string, unknown>;
}

export const dashboardApi = {
  ensure(
    config: ApiClientConfig,
    input: EnsureDashboardInput,
  ): Promise<DashboardRecord> {
    return apiCall(config, "POST", "/v1/dashboards/ensure", input);
  },

  get(config: ApiClientConfig, dashboardId: string): Promise<DashboardRecord> {
    return apiCall(config, "GET", `/v1/dashboards/${seg(dashboardId)}`);
  },

  update(
    config: ApiClientConfig,
    dashboardId: string,
    input: { expectedDashboardVersion: number; title?: string },
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "PATCH",
      `/v1/dashboards/${seg(dashboardId)}`,
      input,
    );
  },

  createPage(
    config: ApiClientConfig,
    dashboardId: string,
    input: { expectedDashboardVersion: number; title: string },
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "POST",
      `/v1/dashboards/${seg(dashboardId)}/pages`,
      input,
    );
  },

  updatePage(
    config: ApiClientConfig,
    dashboardId: string,
    pageId: string,
    input: {
      expectedDashboardVersion: number;
      expectedPageVersion: number;
      title?: string;
      isDefault?: boolean;
    },
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "PATCH",
      `/v1/dashboards/${seg(dashboardId)}/pages/${seg(pageId)}`,
      input,
    );
  },

  deletePage(
    config: ApiClientConfig,
    dashboardId: string,
    pageId: string,
    guards: { expectedDashboardVersion: number; expectedPageVersion: number },
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "DELETE",
      `/v1/dashboards/${seg(dashboardId)}/pages/${seg(pageId)}`,
      undefined,
      {
        expectedDashboardVersion: String(guards.expectedDashboardVersion),
        expectedPageVersion: String(guards.expectedPageVersion),
      },
    );
  },

  reorderPages(
    config: ApiClientConfig,
    dashboardId: string,
    input: { expectedDashboardVersion: number; pageIds: readonly string[] },
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "PUT",
      `/v1/dashboards/${seg(dashboardId)}/pages/order`,
      input,
    );
  },

  createWidgetInstance(
    config: ApiClientConfig,
    dashboardId: string,
    pageId: string,
    input: CreateWidgetInstanceInput,
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "POST",
      `/v1/dashboards/${seg(dashboardId)}/pages/${seg(pageId)}/widget-instances`,
      input,
    );
  },

  updateWidgetInstance(
    config: ApiClientConfig,
    dashboardId: string,
    widgetInstanceId: string,
    input: UpdateWidgetInstanceInput,
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "PATCH",
      `/v1/dashboards/${seg(dashboardId)}/widget-instances/${seg(widgetInstanceId)}`,
      input,
    );
  },

  deleteWidgetInstance(
    config: ApiClientConfig,
    dashboardId: string,
    widgetInstanceId: string,
    guards: { expectedDashboardVersion: number; expectedPageVersion: number },
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "DELETE",
      `/v1/dashboards/${seg(dashboardId)}/widget-instances/${seg(widgetInstanceId)}`,
      undefined,
      {
        expectedDashboardVersion: String(guards.expectedDashboardVersion),
        expectedPageVersion: String(guards.expectedPageVersion),
      },
    );
  },

  updateWidgetLayout(
    config: ApiClientConfig,
    dashboardId: string,
    pageId: string,
    input: WidgetLayoutInput,
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "PUT",
      `/v1/dashboards/${seg(dashboardId)}/pages/${seg(pageId)}/widget-layout`,
      input,
    );
  },

  putConnection(
    config: ApiClientConfig,
    dashboardId: string,
    widgetInstanceId: string,
    slotKey: string,
    input: PutConnectionInput,
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "PUT",
      `/v1/dashboards/${seg(dashboardId)}/widget-instances/${seg(widgetInstanceId)}/connections/${seg(slotKey)}`,
      input,
    );
  },

  deleteConnection(
    config: ApiClientConfig,
    dashboardId: string,
    widgetInstanceId: string,
    slotKey: string,
    guards: {
      expectedDashboardVersion: number;
      expectedConnectionVersion: number;
    },
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "DELETE",
      `/v1/dashboards/${seg(dashboardId)}/widget-instances/${seg(widgetInstanceId)}/connections/${seg(slotKey)}`,
      undefined,
      {
        expectedDashboardVersion: String(guards.expectedDashboardVersion),
        expectedConnectionVersion: String(guards.expectedConnectionVersion),
      },
    );
  },

  resolveWidgetData(
    config: ApiClientConfig,
    dashboardId: string,
    input: WidgetDataResolveRequest,
    signal?: AbortSignal,
  ): Promise<WidgetDataResolveResponse> {
    return apiCall(
      config,
      "POST",
      `/v1/dashboards/${seg(dashboardId)}/widget-data/resolve`,
      input,
      undefined,
      signal,
    );
  },

  upgradeTemplate(
    config: ApiClientConfig,
    dashboardId: string,
    input: { expectedDashboardVersion: number; toVersion: number },
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "POST",
      `/v1/dashboards/${seg(dashboardId)}/template-upgrades`,
      input,
    );
  },

  migrateConnections(
    config: ApiClientConfig,
    dashboardId: string,
    input: {
      expectedDashboardVersion: number;
      providerKey: string;
      datasetKey: string;
      toVersion: number;
    },
  ): Promise<DashboardRecord> {
    return apiCall(
      config,
      "POST",
      `/v1/dashboards/${seg(dashboardId)}/connection-migrations`,
      input,
    );
  },
};
